import { Box, Button, chakra, Tooltip } from '@chakra-ui/react';
import { t } from 'i18next';
import { useTheme } from '~components/ui/styles/hooks';
import { ScheduleTimeBlockInput } from '~graphql/__generated__/types';
import { hourToDecimal } from '~utils/timeBlocks';
import { MAX_PRIORITY_LANES } from '../scheduleTimeBlocks';
import { describeTimeBlockData, TIME_BLOCK_CONTENT_COLOR, TIME_BLOCK_META } from '../timeBlockMeta';
import { AppOption, PlaylistOption } from '../useEditScheduleTimeBlocks';

/**
 * Block metrics, and the widths at which content drops out. The design's
 * "Progression of time-slot" shows three stages - icon plus full time, icon plus
 * truncated time, then icon only - with an empty bar once even the icon will not
 * fit. The thresholds are derived from these metrics rather than hardcoded, so they
 * stay correct if the padding changes.
 */
export const BLOCK_PADDING_LEFT = 12;
export const BLOCK_PADDING_RIGHT = 8;
export const BLOCK_ICON_SIZE = 24;
export const BLOCK_GAP = 8;
export const MIN_TIME_TEXT_WIDTH = 48;

export const ICON_ONLY_MIN_WIDTH = BLOCK_PADDING_LEFT + BLOCK_ICON_SIZE + BLOCK_PADDING_RIGHT;
export const WITH_TIME_MIN_WIDTH = ICON_ONLY_MIN_WIDTH + BLOCK_GAP + MIN_TIME_TEXT_WIDTH;

interface Props {
  currentTimeBlock: ScheduleTimeBlockInput;
  playlists: PlaylistOption[];
  apps: AppOption[];
  isDisabled?: boolean;
  /** Pixel width of the whole day, so the block can size itself in real units. */
  timelineWidth: number;
  /** Opens the day's stacked editor with this slot already selected. */
  onSelect: (timeBlock: ScheduleTimeBlockInput) => void;
}

export const ScheduleTimeBlockBox = ({
  currentTimeBlock,
  playlists,
  apps,
  isDisabled,
  timelineWidth,
  onSelect,
}: Props) => {
  const theme = useTheme();

  const { start, end, type, priority } = currentTimeBlock;
  const width = (hourToDecimal('end', end) - hourToDecimal('start', start)) * 100;
  const meta = TIME_BLOCK_META[type];
  const BlockIcon = meta.blockIcon;
  const payload = describeTimeBlockData(
    type,
    currentTimeBlock.data,
    (playlistId) => playlists.find((p) => p.id === playlistId)?.title,
    (applicationId) => apps.find((app) => app.applicationId === applicationId)?.name,
  );

  const blockWidth = (width / 100) * timelineWidth;
  // Until the timeline has been measured, assume there is room rather than flashing empty.
  const showIcon = timelineWidth === 0 || blockWidth >= ICON_ONLY_MIN_WIDTH;
  const showTime = timelineWidth === 0 || blockWidth >= WITH_TIME_MIN_WIDTH;

  // With no time text to lead, the icon is the whole content, so it centres instead
  // of sitting against the left padding.
  const isIconOnly = showIcon && !showTime;

  const timeRange = `${start} - ${end === '00:00' ? '24:00' : end}`;
  const tooltipLabel = [t(meta.label), payload].filter(Boolean).join(' — ') + ` · ${timeRange}`;

  return (
    <Tooltip label={tooltipLabel} hasArrow>
      <Box
        position="absolute"
        top={0}
        height="100%"
        left={`${hourToDecimal('start', start) * 100}%`}
        width={`${width}%`}
        // Of the slots covering a given minute the lowest-numbered one plays, so a
        // lower priority paints over a higher one.
        zIndex={MAX_PRIORITY_LANES + 1 - priority}
      >
        <Button
          onClick={() => onSelect(currentTimeBlock)}
          variant="unstyled"
          overflow="hidden"
          backgroundColor={meta.background}
          color={TIME_BLOCK_CONTENT_COLOR}
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent={isIconOnly ? 'center' : 'left'}
          gap={`${BLOCK_GAP}px`}
          borderRadius={4}
          // Shorthand on purpose: the `unstyled` variant sets `p: 0`, which wins over
          // paddingLeft/paddingRight longhands.
          padding={isIconOnly ? 0 : `0 ${BLOCK_PADDING_RIGHT}px 0 ${BLOCK_PADDING_LEFT}px`}
          transition="all 200ms ease"
          isDisabled={isDisabled}
          boxShadow={[
            'inset 0px 0px 0px 2px transparent',
            '0px 10px 15px -3px rgba(0, 56, 95, 0)',
            '0px 4px 6px -2px rgba(0, 56, 95, 0)',
          ].join(', ')}
          sx={{
            _hover: {
              boxShadow: [
                `inset 0px 0px 0px 2px ${theme.colors.blue[500]}`,
                '0px 10px 15px -3px rgba(0, 56, 95, 0.1)',
                '0px 4px 6px -2px rgba(0, 56, 95, 0.05)',
              ].join(', '),
            },
          }}
        >
          {showIcon && (
            <BlockIcon
              width={`${BLOCK_ICON_SIZE}px`}
              height={`${BLOCK_ICON_SIZE}px`}
              flexShrink={0}
              color={meta.blockIconColor}
            />
          )}
          {showTime && (
            <chakra.span
              fontWeight="normal"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {timeRange}
            </chakra.span>
          )}
        </Button>
      </Box>
    </Tooltip>
  );
};
